/**
 * Загружает шрифт в document.fonts если его еще нет
 */
export async function loadFont(fontFamily: string, fontUrl: string): Promise<void> {
  // Проверяем загружен ли шрифт уже
  const fonts = Array.from(document.fonts);
  const isFontLoaded = fonts.some(font => font.family === fontFamily);
  
  if (isFontLoaded) {
    console.log(`✅ Font "${fontFamily}" уже загружен`);
    return;
  }
  
  try {
    console.log(`📥 Загружаем шрифт "${fontFamily}" из ${fontUrl}`);
    const fontFace = new FontFace(fontFamily, `url(${fontUrl})`);
    const loadedFont = await fontFace.load();
    document.fonts.add(loadedFont);
    console.log(`✅ Шрифт "${fontFamily}" успешно загружен`);
  } catch (error) {
    console.error(`❌ Ошибка загрузки шрифта "${fontFamily}":`, error);
    throw error;
  }
}

/** Сколько раз пробуем скачать шрифт, прежде чем признать фолбэк. */
const FONT_LOAD_ATTEMPTS = 3;

/**
 * Гарантирует, что шрифт доступен ДЛЯ CANVAS, включая iOS WKWebView (Telegram на iPhone).
 * Safari-WebView не видит шрифт, добавленный через document.fonts, пока он не «прогрет»:
 * нужно await document.fonts.load(...) и реально отрендерить текст в DOM.
 *
 * Возвращает, доступно ли запрошенное семейство на самом деле. false — все
 * попытки загрузки провалились и рисование уйдёт системным фолбэком. Что с
 * этим делать, решает вызывающий: превью рисует как есть, а экспорт поста
 * обязан остановиться — тонкий системный шрифт уже уходил в опубликованные
 * посты незамеченным (отзыв редакции 04.09, shymda_).
 */
export async function ensureCanvasFont(family: string, fontUrl?: string, warmSizePx = 64): Promise<boolean> {
  try {
    if (fontUrl) {
      const already = Array.from(document.fonts).some((f) => f.family === family);
      if (!already) {
        // Одна сетевая попытка флакает — повторяем с паузой, прежде чем сдаться.
        for (let attempt = 1; attempt <= FONT_LOAD_ATTEMPTS; attempt++) {
          try {
            const ff = new FontFace(family, `url(${fontUrl})`);
            await ff.load();
            document.fonts.add(ff);
            break;
          } catch (e) {
            if (attempt === FONT_LOAD_ATTEMPTS) throw e;
            await new Promise((r) => setTimeout(r, 300 * attempt));
          }
        }
      }
    }
    await document.fonts.load(`${warmSizePx}px "${family}"`);
    await document.fonts.ready;
  } catch (e) {
    console.warn('ensureCanvasFont: load failed for', family, e);
  }
  // Прогрев для WKWebView: рендерим скрытый текст этим шрифтом, чтобы canvas его увидел.
  try {
    const el = document.createElement('span');
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = `position:fixed;left:-9999px;top:-9999px;visibility:hidden;font-size:${warmSizePx}px;font-family:"${family}";`;
    el.textContent = 'AaBbGg0123АаБбВвГг';
    document.body.appendChild(el);
    void el.offsetWidth;
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    el.remove();
  } catch {
    /* ignore */
  }
  // Реально ли семейство доступно. Вне браузера (тесты, среды без
  // document.fonts.check) считаем, что да, — там и фолбэка не бывает.
  try {
    if (fontUrl && typeof document.fonts?.check === 'function') {
      return document.fonts.check(`${warmSizePx}px "${family}"`);
    }
  } catch {
    /* ignore */
  }
  return true;
}

/**
 * Загружает шрифт если есть fontUrl, иначе пропускает (используется системный шрифт)
 */
export async function ensureFontLoaded(fontFamily: string, fontUrl?: string): Promise<void> {
  if (!fontUrl) {
    console.log(`ℹ️ fontUrl не указан для "${fontFamily}", используется системный шрифт`);
    return;
  }
  
  // Проверяем является ли это кастомным шрифтом
  if (!fontFamily.startsWith('CustomFont_')) {
    console.log(`ℹ️ "${fontFamily}" - системный шрифт, загрузка не требуется`);
    return;
  }
  
  await loadFont(fontFamily, fontUrl);
}
